// libraries.
import { useState } from "react";

// store.
import { useUserProfile } from "@shared/GoogleLoginButton/user-slice/useUserProfile.hook";
import { useRulesSlice } from "@store/apiSlices/incentiveRules/slices/rules";

// interfaces.
import { RawRuleItem } from "@store/apiSlices/incentiveRules/interfaces/rawRules.interfaces";

// styles.
import { StyledSubRutaDashboard } from "../styles";

// components.
import { GanadorPanel } from "./GanadorPanel";
import { ContenderPanel } from "./ContenderPanel";
import { ConfianzaPanel } from "./ConfianzaPanel";
import { PromoteModal } from "./PromoteModal";
import { ConfirmToggleModal } from "./ConfirmToggleModal";
import { ConfirmLockModal } from "./ConfirmLockModal";
import { CreateRuleModal } from "./CreateRuleModal";

// ─── Types ────────────────────────────────────────────────────────────────────

type PanelSide = 1 | 2; // 1 = Winner, 2 = Contender

interface SubRutaDashboardProps {
  subRutaId: number;
  subRutaName: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const SubRutaDashboard = ({ subRutaId, subRutaName }: SubRutaDashboardProps) => {
  const { country, email } = useUserProfile();
  const resolvedCountry = country || "CO";
  const { getRawRulesForCountry, toggleRule, promoteContenders, lockWinners, isSaving } = useRulesSlice();

  const allRules = getRawRulesForCountry(resolvedCountry);
  const subRutaRules = allRules.filter((r) => r.sub_ruta_id === subRutaId);

  const ganadores  = subRutaRules.filter((r) => r.state_route === 1);
  const contenders = subRutaRules.filter((r) => r.state_route === 2);
  const confianza  = subRutaRules.filter((r) => r.state_route === 3);
  const canPromote = contenders.some((r) => r.is_active === "active");

  // ── Modal: Promote ─────────────────────────────────────────────────────────
  const [showPromoteModal, setShowPromoteModal] = useState(false);

  const handlePromoteConfirm = async () => {
    // El modal controla su propio cierre tras la animación de éxito.
    await promoteContenders(ganadores, contenders, resolvedCountry, email ?? "");
  };

  // ── Modal: Toggle ───────────────────────────────────────────────────────────
  const [toggleTarget, setToggleTarget] = useState<RawRuleItem | null>(null);

  const handleToggleConfirm = async () => {
    if (!toggleTarget) return;
    await toggleRule(toggleTarget, resolvedCountry, email ?? "");
    setToggleTarget(null);
  };

  // ── Modal: Lock → Confianza ─────────────────────────────────────────────────
  const [showLockModal, setShowLockModal] = useState(false);

  const handleLockConfirm = async () => {
    await lockWinners(ganadores, resolvedCountry, email ?? "");
    setShowLockModal(false);
  };

  // ── Modal: Editar ───────────────────────────────────────────────────────────
  const [ruleModal, setRuleModal] = useState<{
    show: boolean;
    stateRoute: PanelSide;
    editRule: RawRuleItem | null;
  }>({ show: false, stateRoute: 1, editRule: null });

  const openEdit = (rule: RawRuleItem) =>
    setRuleModal({
      show: true,
      stateRoute: (rule.state_route ?? 1) as PanelSide,
      editRule: rule,
    });

  const closeRuleModal = () =>
    setRuleModal((prev) => ({ ...prev, show: false, editRule: null }));

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <StyledSubRutaDashboard>
        {/* ── Fila superior: Winner | Contender ──────────────────────────── */}
        <div className="dashboard-top-row">
          <GanadorPanel
            rules={ganadores}
            canLock={ganadores.length > 0 && !isSaving}
            onLock={() => setShowLockModal(true)}
            onEdit={openEdit}
            onToggle={(rule) => setToggleTarget(rule)}
          />
          <ContenderPanel
            experiments={contenders}
            canPromote={canPromote && !isSaving}
            onPromote={() => setShowPromoteModal(true)}
            onEdit={openEdit}
            onToggle={(rule) => setToggleTarget(rule)}
          />
        </div>

        {/* ── Fila inferior: Confianza (state_route=3) ────────────────────── */}
        {confianza.length > 0 && (
          <div className="dashboard-bottom-row">
            <ConfianzaPanel
              rules={confianza}
              onEdit={openEdit}
              onToggle={(rule) => setToggleTarget(rule)}
            />
          </div>
        )}
      </StyledSubRutaDashboard>

      <PromoteModal
        show={showPromoteModal}
        subRutaName={subRutaName}
        contenders={contenders}
        ganadores={ganadores}
        onConfirm={handlePromoteConfirm}
        onClose={() => setShowPromoteModal(false)}
      />

      <ConfirmToggleModal
        rawRule={toggleTarget}
        onConfirm={handleToggleConfirm}
        onClose={() => setToggleTarget(null)}
      />

      <ConfirmLockModal
        show={showLockModal}
        subRutaName={subRutaName}
        ganadores={ganadores}
        onConfirm={handleLockConfirm}
        onClose={() => setShowLockModal(false)}
      />

      {/* Modal de editar — solo se abre desde los botones ✏️ de cada panel */}
      {ruleModal.editRule && (
        <CreateRuleModal
          show={ruleModal.show}
          country={resolvedCountry}
          subRutaId={subRutaId}
          stateRoute={ruleModal.stateRoute}
          editRule={ruleModal.editRule}
          onClose={closeRuleModal}
        />
      )}
    </>
  );
};
